import { type ButtonHTMLAttributes, forwardRef } from 'react';

type Variant = 'gradient' | 'primary' | 'ghost' | 'outline' | 'danger';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  isLoading?: boolean;
}

const sizes: Record<Size, string> = {
  sm: 'h-9 px-4 text-sm rounded-xl',
  md: 'h-11 px-6 text-base rounded-2xl',
  lg: 'h-14 px-8 text-lg rounded-2xl',
};

const variants: Record<Variant, string> = {
  gradient:
    'bg-brand-gradient text-white shadow-lift hover:shadow-glow active:scale-[0.98]',
  primary:
    'bg-ink text-paper hover:bg-ink-700 active:scale-[0.98]',
  ghost:
    'bg-transparent text-ink hover:bg-paper-200 active:scale-[0.98]',
  outline:
    'bg-paper-50 text-ink border border-paper-300 hover:border-ink hover:bg-paper-100 active:scale-[0.98]',
  danger:
    'bg-danger text-white hover:opacity-90 active:scale-[0.98]',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className = '', variant = 'gradient', size = 'md', isLoading, disabled, children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || isLoading}
      className={`inline-flex items-center justify-center gap-2 font-sans font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${sizes[size]} ${variants[variant]} ${className}`}
      {...rest}
    >
      {isLoading && (
        <span className="inline-block h-4 w-4 rounded-full border-2 border-current border-r-transparent animate-spin" />
      )}
      {children}
    </button>
  );
});
